import { useState, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import type { ProyectoCorreo } from '@/lib/types'
import { Button } from '@/components/ui'
import { IconSend, IconX, IconAlertTriangle } from '@/components/icons/Icons'
import { nombreDe } from '@/lib/correo-utils'

/*
  Responder un hilo archivado.

  Dos cosas son deliberadas y conviene no "mejorarlas" sin pensarlo:

  1. No hay campo para escribir una dirección. Los destinatarios son los del
     hilo y nada más; el backend rechaza cualquier otro. (Para empezar un hilo
     nuevo está NuevoCorreoModal, que tiene su propio candado: los contactos
     de la ficha.)
  2. Hay una confirmación antes de enviar. El correo sale con el nombre de la
     empresa hacia un cliente y no se puede deshacer: no debería poder irse de
     un clic distraído.
*/
interface Props {
  basePath: string
  /** Cómo se llama la ficha en los textos: «proyecto», «oportunidad». */
  correo: ProyectoCorreo
  onEnviado: () => void
}

export default function ResponderCorreo({ basePath, correo, onEnviado }: Props) {
  const api = useApi()
  const candidatos = correo.responder
  const [abierto, setAbierto] = useState(false)
  const [cuerpo, setCuerpo] = useState('')
  const [para, setPara] = useState<string[]>([])
  const [cc, setCc] = useState<string[]>([])
  const [confirmando, setConfirmando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // El id se fija al abrir el editor, no al enviar: así un segundo clic manda
  // el mismo id y el backend lo reconoce en vez de mandar el correo dos veces.
  const envioId = useMemo(
    () => `${correo.id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    [correo.id, abierto], // eslint-disable-line react-hooks/exhaustive-deps
  )

  function abrir() {
    setPara(candidatos?.para || [])
    setCc([])
    setCuerpo('')
    setError(null)
    setConfirmando(false)
    setAbierto(true)
  }

  function alternar(lista: string[], set: (v: string[]) => void, dir: string) {
    set(lista.includes(dir) ? lista.filter(d => d !== dir) : [...lista, dir])
  }

  async function enviar() {
    setEnviando(true)
    setError(null)
    try {
      await api.post(`${basePath}/${correo.id}/responder`, {
        cuerpo, para, cc, envio_id: envioId,
      })
      setAbierto(false)
      onEnviado()
    } catch (e: any) {
      setError(e?.message || 'No se pudo enviar la respuesta.')
      setConfirmando(false)
    } finally {
      setEnviando(false)
    }
  }

  if (!candidatos?.puede) {
    // Se dice por qué, en vez de esconder el botón sin explicación. La causa
    // casi siempre es un hilo archivado antes de que se guardara el Message-ID,
    // y se arregla con el botón de actualizar que está arriba.
    return (
      <p className="text-muted" style={{ fontSize: 13, marginTop: 12 }}>
        Para responder este hilo hay que actualizarlo primero (le falta el identificador del último mensaje).
      </p>
    )
  }

  if (!abierto) {
    return (
      <div style={{ marginTop: 12 }}>
        <Button onClick={abrir}><IconSend size={14} /> Responder</Button>
      </div>
    )
  }

  const todas = [...(candidatos.para || []), ...(candidatos.cc || [])]

  return (
    <div className="card" style={{ marginTop: 12, padding: 14 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <span className="text-muted" style={{ fontSize: 13 }}>Para:</span>
        {todas.map(d => {
          const enPara = para.includes(d)
          const enCc = cc.includes(d)
          return (
            <button
              key={d}
              className={`chip-destino ${enPara ? 'is-para' : enCc ? 'is-cc' : 'is-fuera'}`}
              title={enPara ? 'Recibe la respuesta — clic para pasarlo a copia' : enCc ? 'Va en copia — clic para sacarlo' : 'No recibe esta respuesta — clic para ponerlo en copia'}
              onClick={() => {
                if (enPara) { setPara(para.filter(x => x !== d)); alternar(cc, setCc, d) }
                else if (enCc) setCc(cc.filter(x => x !== d))
                else alternar(cc, setCc, d)
              }}
            >
              {nombreDe(d)}
            </button>
          )
        })}
      </div>

      <textarea
        className="form-input"
        rows={7}
        placeholder="Escribí tu respuesta…"
        value={cuerpo}
        onChange={e => setCuerpo(e.target.value)}
        style={{ width: '100%', resize: 'vertical' }}
        autoFocus
      />

      <p className="text-muted" style={{ fontSize: 12.5, marginTop: 6 }}>
        Sale desde el buzón conectado, dentro de este hilo, con la firma de la empresa.
      </p>

      {error && (
        <p style={{ color: 'var(--danger)', fontSize: 13, marginTop: 8 }}>
          <IconAlertTriangle size={13} /> {error}
        </p>
      )}

      {confirmando ? (
        <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
          <p style={{ fontSize: 14, marginBottom: 4 }}>
            <b>Se va a enviar a:</b> {para.join(', ') || '(nadie)'}
            {cc.length > 0 && <> · <b>copia:</b> {cc.join(', ')}</>}
          </p>
          <p className="text-muted" style={{ fontSize: 13, marginBottom: 10 }}>
            El correo sale ahora y no se puede deshacer.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={enviar} disabled={enviando}>
              <IconSend size={14} /> {enviando ? 'Enviando…' : 'Confirmar y enviar'}
            </Button>
            <button className="btn btn-secondary" onClick={() => setConfirmando(false)} disabled={enviando}>
              Volver a editar
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button onClick={() => setConfirmando(true)} disabled={!cuerpo.trim() || para.length === 0}>
            <IconSend size={14} /> Revisar y enviar
          </Button>
          <button className="btn btn-secondary" onClick={() => setAbierto(false)}>
            <IconX size={14} /> Cancelar
          </button>
        </div>
      )}
    </div>
  )
}
